<script setup>
import { computed, ref, watch } from "vue";
import { Star } from "@element-plus/icons-vue";
import { socialFundFeatures, tireOptions, isValidTires } from "@/utils/socialFundV2";

// Valores de App\Enum\SocialFundA51Resolution — mismo catálogo que el
// filtro de SocialFundRequests.vue (a51ResolutionOptions), sin enum JS
// compartido en el proyecto.
const A51_RESOLUTION_OPTIONS = [
  { value: "approved", label: "Aprobada" },
  { value: "rejected", label: "Denegada" },
  { value: "undecided", label: "En duda" },
];

// Único tipo del catálogo que lleva cuantía (App\Service\SocialFundRequiredDocumentsResolver
// y App\Controller\SocialFundApplicationManagementController::updateClassification()
// exigen requestedAmount al cambiar A este tipo).
const AMOUNT_APPLICATION_TYPE_CODE = "aportacion_economica";

// Mismos tramos fijos que el select "Importe solicitado" del formulario
// público de WordPress (grupoandres.com) — el importe de una aportación
// económica no es libre, se elige entre estos 6 valores. Etiquetas fijas
// (no toLocaleString): en es-ES, Number.prototype.toLocaleString solo
// agrupa por millares a partir de 10000 (minimumGroupingDigits del locale),
// dejando "1000"/"2500"/"5000" sin el punto que sí lleva el formulario público.
const AMOUNT_OPTIONS = [
  { value: 250, label: "250 €" },
  { value: 500, label: "500 €" },
  { value: 1000, label: "1.000 €" },
  { value: 2500, label: "2.500 €" },
  { value: 5000, label: "5.000 €" },
  { value: 10000, label: "10.000 €" },
];
const AMOUNT_VALUES = AMOUNT_OPTIONS.map((option) => option.value);

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  reference: { type: String, required: true },
  applicationTypeCode: { type: String, default: null },
  a51ResolutionCode: { type: String, default: null },
  requestedAmount: { type: [String, Number], default: null },
  requestedTires: { type: Number, default: null },
  entityName: { type: String, default: null },
  projectDescription: { type: String, default: null },
  amountJustification: { type: String, default: null },
  applicationTypeOptions: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "save"]);

const form = ref({
  applicationType: null,
  a51Resolution: null,
  requestedAmount: null,
  entityName: "",
  projectDescription: "",
  amountJustification: "",
});
const amountError = ref("");
const tiresError = ref("");
const showTiresField = computed(() => socialFundFeatures.has("tires") && form.value.applicationType === "solicitud_neumaticos");
const projectDescriptionError = ref("");

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const showAmountField = computed(() => form.value.applicationType === AMOUNT_APPLICATION_TYPE_CODE);

const resetForm = () => {
  // El importe llega del backend como string decimal (ej. "1000.00"); se
  // normaliza a número para que el <el-select> pueda preseleccionar el
  // tramo correspondiente. Si el valor existente no coincide con ningún
  // tramo (datos históricos/importados con importe libre), no se preselecciona
  // nada y hay que elegir uno de los 6 tramos válidos.
  const currentAmount = props.requestedAmount !== null && props.requestedAmount !== undefined
    ? Number(props.requestedAmount)
    : null;
  form.value = {
    applicationType: props.applicationTypeCode || null,
    requestedTires: props.requestedTires,
    a51Resolution: props.a51ResolutionCode || null,
    requestedAmount: AMOUNT_VALUES.includes(currentAmount) ? currentAmount : null,
    entityName: props.entityName || "",
    projectDescription: props.projectDescription || "",
    amountJustification: props.amountJustification || "",
  };
  amountError.value = "";
  tiresError.value = "";
  projectDescriptionError.value = "";
};

watch(
  () => props.modelValue,
  (isVisible) => {
    if (isVisible) resetForm();
  }
);

const close = () => {
  if (props.saving) return;
  visible.value = false;
};

const save = () => {
  amountError.value = "";
  projectDescriptionError.value = "";
  if (showAmountField.value && !form.value.requestedAmount) {
    amountError.value = "La cuantía solicitada es obligatoria para \"Aportación económica\".";
  }
  // El backend exige projectDescription no vacío si se envía (400 si es "").
  if (!form.value.projectDescription.trim()) {
    projectDescriptionError.value = "La descripción del proyecto es obligatoria.";
  }
  tiresError.value = showTiresField.value && !isValidTires(form.value.requestedTires) ? "Selecciona una cantidad par de 2 a 16." : "";
  // Comprobar todos los campos antes de bloquear el guardado.
  if (amountError.value || projectDescriptionError.value || tiresError.value) return;

  // Solo se incluyen las claves con valor: el backend distingue "no
  // enviado" (sin cambios) de "enviado a null" (rechazado como inválido,
  // ver SocialFundApplicationManagementController::updateProject()).
  const payload = {};
  if (form.value.applicationType) payload.applicationType = form.value.applicationType;
  if (form.value.a51Resolution) payload.a51Resolution = form.value.a51Resolution;
  if (showAmountField.value) payload.requestedAmount = form.value.requestedAmount;
  if (showTiresField.value) payload.requestedTires = form.value.requestedTires;
  // entityName/amountJustification: siempre se envían (aceptan "" para vaciar).
  payload.entityName = form.value.entityName.trim();
  payload.projectDescription = form.value.projectDescription.trim();
  payload.amountJustification = form.value.amountJustification.trim();
  emit("save", payload);
};
</script>

<template>
  <el-dialog v-model="visible" width="600px" :show-close="false" :lock-scroll="true"
    class="social-fund-classification-dialog" @closed="resetForm">
    <section v-if="visible" class="social-fund-classification-dialog__content">
      <img class="social-fund-classification-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" :class="{ 'social-fund-classification-dialog__close--disabled': saving }" @click="close" />

      <header class="social-fund-classification-dialog__header">
        <el-icon>
          <Star />
        </el-icon>
        <div>
          <h2>Editar solicitud</h2>
          <p>Solicitud {{ reference }}</p>
        </div>
      </header>

      <div class="row social-fund-classification-dialog__form">
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-type">Tipo de solicitud</label>
            <el-select id="social-fund-classification-type" v-model="form.applicationType" class="w-100"
              placeholder="Selecciona un tipo" :disabled="saving">
              <el-option v-for="option in applicationTypeOptions" :key="option.value" :value="option.value"
                :label="option.label" />
            </el-select>
          </div>
        </div>
        <div v-if="showTiresField" class="col-12">
          <label for="requested-tires">Neumáticos solicitados</label>
          <el-select id="requested-tires" v-model="form.requestedTires" class="w-100" :disabled="saving" @change="tiresError = ''">
            <el-option v-for="number in tireOptions" :key="number" :value="number" :label="String(number)" />
          </el-select><small v-if="tiresError" class="social-fund-classification-dialog__field-error">{{ tiresError }}</small>
        </div>
        <div v-if="showAmountField" class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-amount">Cuantía solicitada</label>
            <el-select id="social-fund-classification-amount" v-model="form.requestedAmount" class="w-100"
              placeholder="Selecciona un tramo" :disabled="saving" @change="amountError = ''">
              <el-option v-for="option in AMOUNT_OPTIONS" :key="option.value" :value="option.value"
                :label="option.label" />
            </el-select>
            <small v-if="amountError" class="social-fund-classification-dialog__field-error">{{ amountError }}</small>
          </div>
        </div>
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-entity">Entidad</label>
            <el-input id="social-fund-classification-entity" v-model="form.entityName" class="w-100"
              placeholder="Nombre de la entidad/club" :disabled="saving" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-description">Descripción del proyecto</label>
            <el-input id="social-fund-classification-description" v-model="form.projectDescription" type="textarea"
              :rows="5" :disabled="saving" @input="projectDescriptionError = ''" />
            <small v-if="projectDescriptionError" class="social-fund-classification-dialog__field-error">{{ projectDescriptionError }}</small>
          </div>
        </div>
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-justification">Justificación del importe</label>
            <el-input id="social-fund-classification-justification" v-model="form.amountJustification" type="textarea"
              :rows="2" :disabled="saving" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-item">
            <label for="social-fund-classification-resolution">Opinión de A51 (no vinculante)</label>
            <el-select id="social-fund-classification-resolution" v-model="form.a51Resolution" class="w-100"
              placeholder="Selecciona una opinión" :disabled="saving">
              <el-option v-for="option in A51_RESOLUTION_OPTIONS" :key="option.value" :value="option.value"
                :label="option.label" />
            </el-select>
          </div>
        </div>
      </div>

      <footer class="social-fund-classification-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="saving" @click="close">CANCELAR</button>
        <button type="button" class="btn btn_red" :disabled="saving" @click="save">
          {{ saving ? "GUARDANDO..." : "GUARDAR CAMBIOS" }}
        </button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-classification-dialog) {
  background: #f5f3ff;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-classification-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-classification-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-classification-dialog__content {
  color: #263052;
  padding: 8px 16px 14px;
  position: relative;
}

.social-fund-classification-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-classification-dialog__close--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-classification-dialog__header {
  align-items: center;
  display: flex;
  gap: 12px;
  padding-right: 48px;
}

.social-fund-classification-dialog__header > .el-icon {
  color: $bg-blue;
  font-size: 31px;
}

.social-fund-classification-dialog__header h2 {
  color: $bg-blue;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -.04em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-classification-dialog__header p {
  color: #737b94;
  font-size: 13px;
  margin: 4px 0 0;
}

.social-fund-classification-dialog__form {
  margin-top: 20px;
  row-gap: 14px;
}

.social-fund-classification-dialog__form .form-item {
  margin-bottom: 0;
}

.social-fund-classification-dialog__form label {
  color: #283150;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-classification-dialog__field-error {
  color: #f9156f;
  display: block;
  font-size: 12px;
  margin-top: 6px;
}

.social-fund-classification-dialog__form :deep(.el-textarea__inner) {
  border-radius: 10px;
  padding: 10px 12px;
}


.social-fund-classification-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin: 20px -16px -14px;
  padding: 17px 16px;
}

.social-fund-classification-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 160px;
}

.social-fund-classification-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-classification-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  :global(.el-dialog.social-fund-classification-dialog) {
    max-height: calc(100vh - 24px);
  }

  .social-fund-classification-dialog :deep(.el-dialog__body) {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }

  .social-fund-classification-dialog__content {
    padding: 18px 16px 14px;
  }

  .social-fund-classification-dialog__close {
    right: 8px;
    top: 7px;
  }

  .social-fund-classification-dialog__header {
    gap: 10px;
    padding-right: 42px;
  }

  .social-fund-classification-dialog__header > .el-icon {
    font-size: 27px;
  }

  .social-fund-classification-dialog__header h2 {
    font-size: 22px;
  }

  .social-fund-classification-dialog__actions {
    flex-direction: column-reverse;
    gap: 10px;
    margin: 20px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-classification-dialog__actions .btn {
    width: 100%;
  }
}
</style>
